/** @fileoverview Renders shared builder navigation and workspace actions. */

import { ArrowLeft, PanelRightClose, PanelRightOpen } from 'lucide-react';
import type { ConnectedSystem } from '../../services/systemsApi';
import type { FabrikAppSummary } from '../../types/apps';
import type { Tenant, User } from '../../types/identity';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import FabrikNavbar, { FabrikNavbarActions } from '../shell/FabrikNavbar';
import PublishControl from './PublishControl';

type Props = {
  appName: string;
  app?: FabrikAppSummary;
  previewOpen: boolean;
  user?: User;
  tenant: Tenant;
  onBack: () => void;
  onTogglePreview: () => void;
  onAppChange: (app: FabrikAppSummary) => void;
  systems: ConnectedSystem[];
};

export default function AppBuilderHeader({
  appName,
  app,
  previewOpen,
  user,
  tenant,
  onBack,
  onTogglePreview,
  onAppChange,
  systems,
}: Props) {
  return (
    <FabrikNavbar
      leading={
        <div className="flex min-w-0 items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-app-text-secondary hover:bg-app-surface-muted-strong hover:text-app-text"
            aria-label="Back to Fabrik"
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="shrink-0 text-base font-semibold text-app-text-muted">Fabrik</span>
          <span className="text-base text-app-text-subtle">/</span>
          <span
            className="min-w-0 max-w-48 truncate text-base font-semibold text-app-text"
            title={appName}
          >
            {appName}
          </span>
        </div>
      }
    >
      <FabrikNavbarActions user={user} tenant={tenant}>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="border-app-border bg-app-surface-card text-app-text-secondary hover:bg-app-surface-muted-strong hover:text-app-text"
                aria-label={previewOpen ? 'Hide side panel' : 'Show side panel'}
                onClick={onTogglePreview}
              >
                {previewOpen ? (
                  <PanelRightClose className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <PanelRightOpen className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </Button>
            }
          />
          <TooltipContent side="bottom">
            {previewOpen ? 'Hide side panel' : 'Show side panel'}
          </TooltipContent>
        </Tooltip>
        <PublishControl app={app} tenant={tenant} systems={systems} onAppChange={onAppChange} />
      </FabrikNavbarActions>
    </FabrikNavbar>
  );
}
